import React, { useState, useEffect, useCallback } from 'react';
import { useApi } from '../../hooks/useApi';
import ClubModal from '../../components/admin/ClubModal';
import ClubEnrollmentModal from '../../components/teacher/ClubEnrollmentModal';
import Modal from '../../components/common/Modal';
import PremiumTooltip from '../../components/common/PremiumTooltip';
import { useAppState } from '../../context/AppStateContext';

const ClubList = () => {
    const { get, del } = useApi();
    const { showToast } = useAppState();
    const [clubs, setClubs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingClub, setEditingClub] = useState(null);
    const [enrollingClub, setEnrollingClub] = useState(null);
    const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
    const [searchQuery, setSearchQuery] = useState('');
    const [clubToDelete, setClubToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const fetchData = useCallback(async () => {
        try {
            const clubData = await get('/api/events/clubs/');
            setClubs(clubData);
        } catch (error) {
            console.error('Error fetching club data:', error);
        } finally {
            setLoading(false);
        }
    }, [get]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleDeleteClick = (club) => {
        setClubToDelete(club);
    };

    const confirmDelete = async () => {
        if (!clubToDelete) return;
        setDeleting(true);
        try {
            await del(`/api/events/clubs/${clubToDelete.id}/`);
            showToast('Activity successfully decommissioned', 'success');
            setClubToDelete(null);
            fetchData();
        } catch (error) {
            console.error('Error deleting club:', error);
            showToast('Failed to remove activity. Please try again.', 'error');
        } finally {
            setDeleting(false);
        }
    };

    const openEditModal = (club) => {
        setEditingClub(club);
        setShowModal(true);
    };

    const filteredClubs = clubs.filter(club =>
        club.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (club.description && club.description.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (club.teacher_name && club.teacher_name.toLowerCase().includes(searchQuery.toLowerCase()))
    );

    const getCategoryBadgeLabel = (cat) => {
        if (!cat) return 'Clubs & Societies';
        const lower = cat.toLowerCase();
        if (lower === 'cocurricular') return 'Co-Curricular';
        if (lower === 'sports') return 'Games & Sports';
        if (lower === 'instruments') return 'Music & Instruments';
        if (lower === 'clubs') return 'Clubs & Societies';
        return cat;
    };

    if (loading) return <div className="py-20 text-center animate-pulse text-[10px] font-black text-slate-300 uppercase tracking-widest">Compiling Extracurricular Registry...</div>;

    return (
        <div className="space-y-10">
            <header className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                <div className="animate-in fade-in slide-in-from-left duration-700">
                    <h1 className="text-3xl font-black text-[#18216D]">Extracurriculars</h1>
                    <p className="text-slate-400 font-bold text-sm mt-1">Governance & Activity Oversight</p>
                </div>
                <div className="flex flex-wrap items-center gap-4 animate-in fade-in slide-in-from-right duration-700">
                    <div className="relative group w-full md:w-64">
                        <i className="fas fa-search absolute left-5 top-1/2 -translate-y-1/2 text-slate-300"></i>
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="SEARCH ACTIVITIES..."
                            className="w-full pl-12 pr-6 py-4 bg-white border-2 border-slate-200 rounded-2xl focus:border-[#FFC425] transition-all text-[10px] font-black uppercase tracking-widest text-[#18216D] placeholder-slate-300 outline-none"
                        />
                    </div>

                    <div className="bg-slate-100/70 p-1.5 rounded-2xl flex items-center border border-slate-200/60 shadow-inner">
                        <button
                            onClick={() => setViewMode('grid')}
                            className={`px-4 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-wider transition-all flex items-center gap-2 ${
                                viewMode === 'grid' 
                                    ? 'bg-[#18216D] text-white shadow-md shadow-indigo-900/20' 
                                    : 'text-slate-400 hover:text-slate-700 hover:bg-slate-200/50'
                            }`}
                            title="Grid Layout"
                        >
                            <i className="fas fa-th-large text-xs"></i>
                            <span>Grid</span>
                        </button>
                        <button
                            onClick={() => setViewMode('list')}
                            className={`px-4 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-wider transition-all flex items-center gap-2 ${
                                viewMode === 'list' 
                                    ? 'bg-[#18216D] text-white shadow-md shadow-indigo-900/20' 
                                    : 'text-slate-400 hover:text-slate-700 hover:bg-slate-200/50'
                            }`}
                            title="List View"
                        >
                            <i className="fas fa-list-ul text-xs"></i>
                            <span>List</span>
                        </button>
                    </div>

                    <button
                        onClick={() => { setEditingClub(null); setShowModal(true); }}
                        className="px-10 py-4 bg-[#18216D] text-white rounded-[1.2rem] shadow-[0_20px_40px_-10px_rgba(24,33,109,0.3)] font-black text-[10px] uppercase tracking-[0.2em] hover:bg-[#1e2985] hover:shadow-indigo-900/40 hover:-translate-y-0.5 transition-all active:scale-95 flex items-center gap-3"
                    >
                        <i className="fas fa-circle-plus text-xs"></i>
                        New Activity
                    </button>
                </div>
            </header>

            {viewMode === 'grid' ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                    {filteredClubs.map((club, idx) => (
                        <div
                            key={club.id}
                            style={{ animationDelay: `${idx * 50}ms` }}
                            className="bg-white rounded-[1.5rem] p-5 border border-slate-100 shadow-[0_6px_25px_rgba(0,0,0,0.02)] hover:shadow-[0_15px_40px_rgba(24,33,109,0.08)] transition-all group relative overflow-hidden animate-in fade-in zoom-in duration-500"
                        >
                            <div className="absolute top-0 right-0 w-28 h-28 bg-indigo-50/20 rounded-full -mr-14 -mt-14 group-hover:scale-150 transition-transform duration-700"></div>

                            {/* Card Decoration */}
                            <div className="absolute top-6 right-6 opacity-5 group-hover:opacity-10 transition-opacity">
                                <i className="fas fa-chess-knight text-5xl text-[#18216D]"></i>
                            </div>

                            <div className="relative z-10">
                                <span className="inline-block px-2.5 py-1 bg-indigo-50 text-indigo-600 rounded-lg text-[7px] font-black uppercase tracking-[0.15em] mb-2">{getCategoryBadgeLabel(club.category)}</span>
                                <h3 className="text-lg font-black text-[#18216D] uppercase tracking-tight group-hover:text-indigo-600 transition-colors">{club.name}</h3>
                                <p className="text-slate-600 text-[10px] font-medium mt-1.5 h-8 overflow-hidden line-clamp-2 leading-relaxed">{club.description || 'Institutional activity briefing.'}</p>

                                <div className="mt-5 flex items-center justify-between border-t border-slate-50 pt-3">
                                    <div>
                                        <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest flex items-center gap-1.5">
                                            <i className="fas fa-user-tie text-[7px]"></i> Patron
                                        </p>
                                        <p className="text-xs font-black text-[#18216D] mt-0.5 italic">{club.teacher_name || 'Vacant'}</p>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Students</p>
                                        <div className="flex items-center justify-end gap-1 mt-0.5">
                                            <span className="text-sm font-black text-[#18216D]">{club.category !== 'cocurricular' ? club.member_count : '-'}</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="mt-5 flex items-center gap-2 transition-all duration-300">
                                    {club.category !== 'cocurricular' && (
                                        <button
                                            onClick={() => setEnrollingClub(club)}
                                            className="flex-1 py-2.5 bg-[#18216D] text-white rounded-xl text-[8px] font-black uppercase tracking-widest hover:bg-[#1e2985] transition-all shadow-md shadow-indigo-900/10 flex items-center justify-center gap-1.5"
                                        >
                                            <i className="fas fa-user-plus text-[8px]"></i>
                                            Enroll Scholars
                                        </button>
                                    )}
                                    <button
                                        onClick={() => openEditModal(club)}
                                        className="h-10 w-10 flex items-center justify-center text-slate-400 hover:text-[#18216D] hover:bg-slate-100 rounded-xl transition-all border border-slate-100"
                                        title="Edit Activity"
                                    >
                                        <i className="fas fa-pen-nib text-xs"></i>
                                    </button>
                                    <button
                                        onClick={() => handleDeleteClick(club)}
                                        className="h-10 w-10 flex items-center justify-center text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-xl transition-all border border-slate-100"
                                        title="Remove Activity"
                                    >
                                        <i className="fas fa-trash-alt text-xs"></i>
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div className="bg-white rounded-[2.5rem] shadow-[0_10px_40px_rgba(0,0,0,0.03)] border border-slate-100 overflow-hidden animate-in fade-in slide-in-from-bottom-8 duration-500">
                    <table className="w-full text-left">
                        <thead className="bg-slate-50/80 border-b border-slate-100">
                            <tr>
                                <th className="px-10 py-8 text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">TITLE</th>
                                <th className="px-10 py-8 text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">DESCRIPTION</th>
                                <th className="px-10 py-8 text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">PATRON</th>
                                <th className="px-10 py-8 text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">STUDENTS</th>
                                <th className="px-10 py-8 text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] text-right">ACTIONS</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-50">
                            {filteredClubs.map(club => (
                                <tr key={club.id} className="hover:bg-slate-50/50 transition-all group">
                                    <td className="px-10 py-8">
                                        <span className="font-black text-[#18216D] text-sm uppercase tracking-tight group-hover:text-indigo-600 transition-colors">{club.name}</span>
                                    </td>
                                    <td className="px-10 py-8">
                                        {club.description ? (
                                            <PremiumTooltip text={club.description} allowWrap={true} position="top">
                                                <p className="text-xs text-slate-600 font-bold line-clamp-1 max-w-xs cursor-pointer">{club.description}</p>
                                            </PremiumTooltip>
                                        ) : (
                                            <span className="text-xs text-slate-400 font-bold">-</span>
                                        )}
                                    </td>
                                    <td className="px-10 py-8">
                                        <span className="text-xs font-black text-[#18216D] italic">{club.teacher_name || 'Vacant'}</span>
                                    </td>
                                    <td className="px-10 py-8">
                                        {club.category !== 'cocurricular' ? (
                                            <button
                                                onClick={() => setEnrollingClub(club)}
                                                className="px-4 py-2 bg-indigo-50/80 hover:bg-[#18216D] text-indigo-700 hover:text-white transition-all rounded-xl text-[10px] font-black uppercase tracking-widest border border-indigo-100/60 shadow-sm flex items-center gap-2 group/btn"
                                            >
                                                <span>{club.member_count} {club.member_count === 1 ? 'Student' : 'Students'}</span>
                                                <i className="fas fa-user-plus text-[9px] group-hover/btn:scale-110 transition-transform"></i>
                                            </button>
                                        ) : (
                                            <span className="text-xs font-bold text-slate-400 pl-3">-</span>
                                        )}
                                    </td>
                                    <td className="px-10 py-8 text-right">
                                        <div className="flex items-center justify-end gap-2.5 transition-all">
                                            {club.category !== 'cocurricular' && (
                                                <button
                                                    onClick={() => setEnrollingClub(club)}
                                                    className="px-3.5 py-2 bg-[#18216D] text-white rounded-xl text-[9px] font-black uppercase tracking-widest hover:bg-[#1e2985] transition-all shadow-md flex items-center gap-1.5"
                                                    title="Enroll Scholars"
                                                >
                                                    <i className="fas fa-user-plus text-[8px]"></i>
                                                    Enroll
                                                </button>
                                            )}
                                            <button
                                                onClick={() => openEditModal(club)}
                                                className="h-10 w-10 flex items-center justify-center text-slate-400 hover:text-[#18216D] hover:bg-slate-100 rounded-xl transition-all border border-slate-100"
                                                title="Edit Activity"
                                            >
                                                <i className="fas fa-pen-nib text-xs"></i>
                                            </button>
                                            <button
                                                onClick={() => handleDeleteClick(club)}
                                                className="h-10 w-10 flex items-center justify-center text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-xl transition-all border border-slate-100"
                                                title="Remove Activity"
                                            >
                                                <i className="fas fa-trash-alt text-xs"></i>
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {filteredClubs.length === 0 && (
                <div className="py-32 text-center animate-in zoom-in duration-500">
                    <div className="w-20 h-20 bg-slate-50 rounded-3xl flex items-center justify-center mx-auto mb-6">
                        <i className="fas fa-magnifying-glass text-slate-200 text-3xl"></i>
                    </div>
                    <p className="text-slate-300 font-black uppercase tracking-[0.3em] text-[10px] italic">No activities match your query</p>
                    <button
                        onClick={() => setSearchQuery('')}
                        className="mt-6 text-[#18216D] font-black text-[10px] uppercase tracking-widest hover:underline underline-offset-8 decoration-indigo-200"
                    >
                        Reset Registry Filters
                    </button>
                </div>
            )}

            {showModal && (
                <ClubModal
                    club={editingClub}
                    onClose={() => setShowModal(false)}
                    onSuccess={fetchData}
                />
            )}

            {enrollingClub && (
                <ClubEnrollmentModal
                    clubId={enrollingClub.id}
                    clubName={enrollingClub.name}
                    onClose={() => setEnrollingClub(null)}
                    onRefresh={fetchData}
                />
            )}

            <Modal
                isOpen={!!clubToDelete}
                onClose={() => setClubToDelete(null)}
                title="Decommission Activity"
                type="danger"
                footer={
                    <>
                        <button
                            onClick={confirmDelete}
                            disabled={deleting}
                            className="flex-1 px-8 py-4 bg-rose-500 text-white rounded-2xl font-black text-[10px] uppercase tracking-[0.2em] hover:bg-rose-600 shadow-xl shadow-rose-900/20 transition-all disabled:opacity-50"
                        >
                            {deleting ? 'Decommissioning...' : 'Confirm Removal'}
                        </button>
                        <button
                            onClick={() => setClubToDelete(null)}
                            className="bg-slate-50 text-slate-400 px-8 py-4 rounded-2xl font-black text-[10px] uppercase tracking-[0.2em] hover:bg-slate-100 transition-colors"
                        >
                            Retain Activity
                        </button>
                    </>
                }
            >
                <p>Are you absolutely certain you wish to decommission the <span className="text-[#18216D] font-black underline underline-offset-4 decoration-rose-500/30 uppercase">{clubToDelete?.name}</span> registry?</p>
                <p className="mt-4 text-sm text-slate-400 font-bold tracking-tight">This action will immediately dissolve the activity and remove all student enrollment records. This procedure is irreversible.</p>
            </Modal>
        </div>
    );
};

export default ClubList;
